import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiFetch } from "@/lib/api.js";
import { useAuth } from "@/auth/useAuth.js";

export const KEY_LOCATIONS = ["ufficio", "proprietario", "cassetta", "collega"] as const;
export type KeyLocation = (typeof KEY_LOCATIONS)[number];

export const KEY_LOCATION_LABEL: Record<KeyLocation, string> = {
  ufficio: "In ufficio",
  proprietario: "Dal proprietario",
  cassetta: "In cassetta",
  collega: "Con un collega",
};

const KeysState = z.object({
  id: z.string().uuid(),
  listing_id: z.string().uuid(),
  location: z.enum(KEY_LOCATIONS),
  lockbox_code: z.string().nullable(),
  holder: z.string().nullable(),
  notes: z.string().nullable(),
  updated_at: z.string(),
});
export type KeysState = z.infer<typeof KeysState>;

const KeyEvent = z.object({
  id: z.string().uuid(),
  listing_id: z.string().uuid(),
  action: z.enum(["presa", "restituita"]),
  person: z.string(),
  happened_at: z.string(),
  notes: z.string().nullable(),
});
export type KeyEvent = z.infer<typeof KeyEvent>;

const KeysResponse = z.object({
  keys: KeysState.nullable(),
  events: z.array(KeyEvent),
  out: z.boolean(),
});

export type KeysInput = {
  location: KeyLocation;
  lockbox_code?: string | null;
  holder?: string | null;
  notes?: string | null;
};

export function useListingKeys(listingId: string | null) {
  const { session } = useAuth();
  const token = session?.access_token;
  return useQuery({
    queryKey: ["keys", listingId],
    enabled: !!token && !!listingId,
    queryFn: () => apiFetch(`/api/v1/admin/listings/${listingId}/keys`, token!, KeysResponse),
  });
}

export function useSaveKeys() {
  const { session } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ listingId, ...input }: KeysInput & { listingId: string }) =>
      apiFetch(`/api/v1/admin/listings/${listingId}/keys`, session!.access_token, KeysState, {
        method: "PUT", body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["keys"] });
      void qc.invalidateQueries({ queryKey: ["listings"] });
    },
  });
}

export function useAddKeyEvent() {
  const { session } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ listingId, ...input }: { listingId: string; action: "presa" | "restituita"; person: string }) =>
      apiFetch(`/api/v1/admin/listings/${listingId}/keys/events`, session!.access_token, KeyEvent, {
        method: "POST", body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["keys"] });
      void qc.invalidateQueries({ queryKey: ["listings"] });
    },
  });
}
